import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown } from '@digdir/designsystemet-react';
import { FilesIcon, MenuElipsisHorizontalIcon, PencilIcon, TrashIcon } from '@navikt/aksel-icons';
import type { SurveyListItem } from '../../../domain/surveys/survey-list';
import classes from '../SurveysPage.module.css';

export type SurveyRowActionHandlers = {
  onCopyLink: (item: SurveyListItem) => void;
  onEditDeadline: (item: SurveyListItem) => void;
  onDelete: (item: SurveyListItem) => void;
};

type SurveyRowActionsProps = SurveyRowActionHandlers & { item: SurveyListItem };

/** Første handling er sekundærknapp, resten er tertiære. Sjeldne handlinger ligger i «…»-menyen. */
export const SurveyRowActions = ({ item, onCopyLink, onEditDeadline, onDelete }: SurveyRowActionsProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const links = [
    { to: item.analysisRoute, label: t('surveys.page.actions.analysis') },
    { to: item.reportRoute, label: t('surveys.page.actions.report') },
    { to: item.detailsRoute, label: t('surveys.page.actions.details') },
    { to: item.resultsRoute, label: t('surveys.page.actions.results') },
  ].filter((link): link is { to: string; label: string } => Boolean(link.to));
  const hasCopy = Boolean(item.answerPath);
  const variantAt = (index: number) => (index === 0 ? 'secondary' : 'tertiary');
  const hasMenu = item.canEditDeadline || item.canDelete;

  const runFromMenu = (action: (item: SurveyListItem) => void) => () => {
    setIsMenuOpen(false);
    action(item);
  };

  return (
    <div className={classes.actions}>
      {hasCopy && (
        <Button
          variant="secondary"
          data-size="sm"
          onClick={() => onCopyLink(item)}
          aria-label={t('surveys.page.actions.copyLinkLabel', { title: item.title })}
        >
          <FilesIcon aria-hidden />
          {t('surveys.page.actions.copyLink')}
        </Button>
      )}
      {links.map((link, index) => (
        <Button key={link.to} asChild variant={variantAt(hasCopy ? index + 1 : index)} data-size="sm">
          <Link to={link.to}>{link.label}</Link>
        </Button>
      ))}
      {hasMenu && (
        <Dropdown.TriggerContext>
          <Dropdown.Trigger
            variant="tertiary"
            data-size="sm"
            icon
            aria-label={t('surveys.page.actions.more', { title: item.title })}
          >
            <MenuElipsisHorizontalIcon aria-hidden />
          </Dropdown.Trigger>
          <Dropdown
            placement="bottom-end"
            open={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
          >
            <Dropdown.List>
              {item.canEditDeadline && (
                <Dropdown.Item>
                  <Dropdown.Button onClick={runFromMenu(onEditDeadline)}>
                    <PencilIcon aria-hidden />
                    {t('surveys.page.actions.editDeadline')}
                  </Dropdown.Button>
                </Dropdown.Item>
              )}
              {item.canDelete && (
                <Dropdown.Item>
                  <Dropdown.Button data-color="danger" onClick={runFromMenu(onDelete)}>
                    <TrashIcon aria-hidden />
                    {t('surveys.page.actions.delete')}
                  </Dropdown.Button>
                </Dropdown.Item>
              )}
            </Dropdown.List>
          </Dropdown>
        </Dropdown.TriggerContext>
      )}
    </div>
  );
};
